/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import { FunctionField, ListContextProvider, ReferenceField, TextField, useTranslate } from 'react-admin';
import { Box, IconButton, Paper, useTheme } from '@material-ui/core';
import keyBy from 'lodash/keyBy';
import { Visibility } from '@material-ui/icons';
import { useAccountInvoiceBalanceListContext } from './AccountInvoiceBalanceProvider';
import Datagrid from '../../../../base/components/ra/data-grid';
import Pagination from '../../../../base/components/customizable-colums/Pagination';
import resourceSlug from '../../../../constant/resource-slug';
import { DATE_DISPLAY_FORMAT } from '../../../../constant';
import AmountField from '../../../../base/components/ra/fields/amount.field';
import { hasPermission } from '../../../../services/util/auth';
import { DateField } from '../../../../base/components/ra/fields';
import { ACCOUNT_STATEMENT_TRANSACTION_TYPE } from '../../../../constant/invoice';
import EmptyTable from '../../../../base/components/guesser/report/emptyTable';

const AccountStatementList = (props) => {
  const t = useTranslate();
  const theme = useTheme();
  const hasReadInvoicePermission = hasPermission(resourceSlug.INVOICE, 'read');
  const hasReadPaymentPermission = hasPermission(resourceSlug.PAYMENT, 'read');

  const { data: accountStatementData, loading, appliedFilters } = useAccountInvoiceBalanceListContext();
  const [ids, setIds] = useState([]);
  const [data, setData] = useState({});
  const [currentIds, setCurrentIds] = useState([]);
  const [currentData, setCurrentData] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const onPageChange = (newPage) => {
    setPage(newPage);
    const startIndex = (newPage - 1) * perPage;
    setCurrentIds(ids.slice(startIndex, startIndex + perPage));
    setCurrentData(keyBy(Object.values(data).slice(startIndex, startIndex + perPage), 'id'));
  };

  const onPerPageChange = (newPerPage) => {
    setPage(1);
    setPerPage(newPerPage);
    setCurrentIds(ids.slice(0, newPerPage));
    setCurrentData(keyBy(Object.values(data).slice(0, newPerPage), 'id'));
  };

  useEffect(() => {
    const list = accountStatementData?.items || [];
    const data = list.reduce((prev, item) => {
      prev[item.transactionId] = {
        ...item,
        id: item.transactionId,
        groupId: appliedFilters.groupId,
      };
      return prev;
    }, {});
    const ids = Object.keys(data);

    setIds(ids);
    setData(data);
    setCurrentIds(ids.slice(0, perPage));
    setCurrentData(keyBy(Object.values(data).slice(0, perPage), 'id'));
  }, [accountStatementData, perPage, appliedFilters]);

  if (!loading && !accountStatementData) {
    return null;
  }

  return (
    <ListContextProvider
      value={{
        ids: currentIds,
        data: currentData,
        loading,
        selectedIds: [],
        currentSort: {
          field: null,
          order: null,
        },
        displayedFilters: null,
        filterValues: null,
        hasCreate: null,
        hideFilter: null,
        onSelect: null,
        onToggleItem: null,
        onUnselectItems: null,
        page,
        perPage,
        resource: resourceSlug.ACCOUNT_STATEMENT,
        setFilters: null,
        setPage: onPageChange,
        setPerPage: onPerPageChange,
        setSort: null,
        showFilter: null,
        total: ids.length,
        syncWithLocation: false,
        ...props,
      }}
    >
      <Box
        component={Paper}
        sx={{
          boxShadow: 'none !important',
          overflow: 'hidden',
          border: `1px solid ${theme.palette.grey[300]}`,
          padding: !accountStatementData?.items?.length ? '30px' : 0,
        }}
      >
        {!accountStatementData?.items?.length ? (
          <EmptyTable
            description={
              Array.isArray(accountStatementData?.items)
                ? t('resources.accountInvoiceBalance.noInvoice')
                : t('resources.accountInvoiceBalance.unavailableFeature')
            }
          />
        ) : (
          <>
            <Datagrid>
              <ReferenceField
                label="resources.group.name"
                sortable={false}
                reference={resourceSlug.GROUP}
                source="groupId"
                link="show"
              >
                <TextField source="name" />
              </ReferenceField>
              <DateField
                source="date"
                sortable={false}
                label="resources.accountInvoiceBalance.fields.date"
                dateFormat={DATE_DISPLAY_FORMAT}
              />
              <FunctionField
                sortable={false}
                label="resources.accountInvoiceBalance.fields.details"
                style={{
                  maxWidth: 200,
                }}
                render={(record) => (
                  <Box
                    mx="auto"
                    sx={{
                      maxWidth: 200,
                    }}
                  >
                    {record.description || '-'}
                  </Box>
                )}
              />
              <AmountField
                source="amount"
                sortable={false}
                label="resources.invoice.fields.amount"
                symbol={() => accountStatementData.currencyCode}
              />
              <FunctionField
                label="resources.accountInvoiceBalance.fields.transactionType"
                sortable={false}
                render={(record) => t(`resources.accountInvoiceBalance.transactionType.${record.transactionType}`)}
              />
              <AmountField
                source="accountBalance"
                sortable={false}
                label="resources.accountInvoiceBalance.fields.accountBalance"
                symbol={() => accountStatementData.currencyCode}
              />

              <FunctionField
                label="ra.field.action"
                sortable={false}
                render={(record) => (
                  <Box display="flex" gap={4} justifyContent="center">
                    {hasReadInvoicePermission &&
                      record.transactionType === ACCOUNT_STATEMENT_TRANSACTION_TYPE.INVOICE && (
                        <Box
                          component={IconButton}
                          size="small"
                          minWidth={30}
                          href={`/#/${resourceSlug.INVOICE}/${record.transactionId}/show`}
                        >
                          <Visibility fontSize="small" />
                        </Box>
                      )}
                    {hasReadPaymentPermission &&
                      record.transactionType === ACCOUNT_STATEMENT_TRANSACTION_TYPE.PAYMENT && (
                        <Box
                          component={IconButton}
                          size="small"
                          minWidth={30}
                          href={`/#/${resourceSlug.PAYMENT}?paymentId=${record.transactionId}`}
                        >
                          <Visibility fontSize="small" />
                        </Box>
                      )}
                  </Box>
                )}
              />
            </Datagrid>
            <Pagination rowsPerPageOptions={[25, 50, 100]} />
          </>
        )}
      </Box>
    </ListContextProvider>
  );
};

AccountStatementList.displayName = 'AccountStatementList';

export default AccountStatementList;
